/**
 * Instructor lookup for the lesson flow — [BUT-843, ours].
 *
 * `LessonTimeStep`, `LessonPaymentStep` and `BookingConfirmationPage` (lesson
 * variant) each repeated the same chain: packs → active pack → club roster
 * (only when the looked-up pack doesn't name the instructor) → session/nav-state
 * sugar → `resolveInstructorDisplay`. This hook is that chain, reproduced exactly —
 * every call site's gating is unchanged, never combined or loosened.
 *
 * The one real difference across call sites is `guestInstructorsFallback`:
 * `LessonTimeStep` is reachable signed-out and keeps the roster query enabled
 * for a guest (`useUserLessons` never answers when disabled, so it can't wait
 * on `hasAnswer`); the other two are reached only once signed in and gate the
 * roster query on `hasAnswer` like any other dependent query.
 *
 * `userLessonId` (booking detail) looks the instructor up in the pack the booking
 * drew on first; `settled` lets a caller hold its render until the lookups answer
 * or fail.
 *
 * @module pages/lessons/useLessonInstructor
 */

import type { ImageData } from '@lgc/database/types/domains/storage';
import { useUserLessons, useClubInstructors } from '~/services/internalApi/lessons';
import type { UseClubInstructorsResult, UserLesson } from '~/services/internalApi/lessons';
import { resolveActivePack, resolveInstructorDisplay } from '@butter/library/lessons';

export interface UseLessonInstructorParams {
  instructorId: number | null;
  clubId: number | undefined;
  isAuthenticated: boolean;
  /** Top-level gate (e.g. `BookingConfirmationPage`'s `isLesson`). @default true */
  enabled?: boolean;
  /** The pack a booked lesson drew on; its whitelist is checked before the active pack's. */
  userLessonId?: number | null;
  /**
   * `LessonTimeStep` only: don't wait on `hasAnswer` for a signed-out guest,
   * since a disabled packs query never answers. @default false
   */
  guestInstructorsFallback?: boolean;
  /** Session/nav-state display sugar — pass `null` unless it still applies. */
  sessionName?: string | null;
  sessionImage?: ImageData | null;
}

export interface UseLessonInstructorResult {
  /** The member's packs at this club. Never undefined. */
  userLessons: UserLesson[];
  userLessonsHasAnswer: boolean;
  activePack: UserLesson | null;
  clubInstructorsQuery: UseClubInstructorsResult;
  instructor: { name: string; image: ImageData | null } | null;
  /** Packs answered or failed, and the roster answered, failed, or was not needed. */
  settled: boolean;
}

export function useLessonInstructor({
  instructorId,
  clubId,
  isAuthenticated,
  enabled = true,
  userLessonId = null,
  guestInstructorsFallback = false,
  sessionName = null,
  sessionImage = null,
}: UseLessonInstructorParams): UseLessonInstructorResult {
  const {
    userLessons,
    hasAnswer: userLessonsHasAnswer,
    isLoading: userLessonsLoading,
  } = useUserLessons({ enabled: enabled && isAuthenticated });
  const activePack = enabled ? resolveActivePack(userLessons) : null;
  // A booked lesson may have drawn on a pack that is no longer the active one.
  const instructorPack =
    userLessonId != null ? (userLessons.find((pack) => pack.id === userLessonId) ?? activePack) : activePack;

  // The roster is needed only when the looked-up pack doesn't name the instructor.
  const rosterEnabled =
    enabled &&
    (guestInstructorsFallback ? (isAuthenticated ? userLessonsHasAnswer : true) : userLessonsHasAnswer) &&
    (instructorPack === null ||
      (instructorId !== null && !instructorPack.allowedInstructors.some((i) => i.id === instructorId)));
  const clubInstructorsQuery = useClubInstructors({ clubId, enabled: rosterEnabled });

  const instructor = enabled
    ? resolveInstructorDisplay({
        instructorId,
        activePack: instructorPack,
        clubInstructors: clubInstructorsQuery.instructors,
        sessionName,
        sessionImage,
      })
    : null;

  // A failed query is "not loading", so a failure settles too and never hides the caller forever.
  const settled =
    (userLessonsHasAnswer || !userLessonsLoading) &&
    (!rosterEnabled || clubInstructorsQuery.hasAnswer || !clubInstructorsQuery.isLoading);

  return { userLessons, userLessonsHasAnswer, activePack, clubInstructorsQuery, instructor, settled };
}
